
  import {
    ComponentPublicInstance,
    computed,
    defineComponent,
    PropType,
    ref,
    shallowRef,
    StyleValue,
    watchEffect,
  } from 'vue';
  import type { VirtualItem } from '@tanstack/virtual-core';
  import { useVirtualizer } from '@tanstack/vue-virtual';
  import axios from 'axios';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import { TArrayAxiosResponse } from '@/common/types/response';

  import Loader from '@/components/Loader.vue';
  import ModalComponent from '@/components/modals/ModalComponent.vue';

  import GlobalContextMenu from './GlobalContextMenu.vue';
  import LocalContextMenu from './LocalContextMenu.vue';
  import {
    LocalContextMenuActions,
    TProductItem,
    TProductsTreeFetcher,
    TTreeSelectionProductsModalData,
  } from './types';

  const DEFAULT_ROW_HEIGHT_PX = 55;
  const VIRTUAL_OVERSCAN_COUNT = 20;

  export default defineComponent({
    name: 'TreeSelectionProductsModal',
    components: {
      ModalComponent,
      Loader,
      ButtonComponent,
      GlobalContextMenu,
      LocalContextMenu,
    },
    emits: ['close', 'select', 'updateSelectedItems'],
    props: {
      isShow: {
        type: Boolean,
        default: false,
      },
      selectedIds: {
        type: Array as PropType<(number | string)[]>,
        default: () => [],
      },
      multiple: {
        type: Boolean,
        default: true,
      },
      selectGroupsOnly: {
        type: Boolean,
        default: false,
      },
      selectLeafsOnly: {
        type: Boolean,
        default: false,
      },
      codes: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      fetcher: {
        type: Function as PropType<TProductsTreeFetcher>,
      },
    },
    data(): TTreeSelectionProductsModalData {
      return {
        items: [],
        searchItems: [],
        visibleItems: [],
        preparedVisibleItems: [],
        mapItems: {},
        request: {
          pageNumber: 1,
          pageSize: 1000000,
          term: '',
        },
        selection: [...this.selectedIds],
        screenWidth: window.innerWidth,
        searchTimeout: null,
        searchWorker: null,
        isLoading: false,
        globalContextMenu: {
          isVisible: false,
          position: { right: 0, top: 0 },
        },
        localContextMenu: {
          isVisible: false,
          position: { right: 0, top: 0 },
          selectedItem: null,
        },
      };
    },
    mounted() {
      this.loadPage();
    },
    beforeUnmount() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchWorker?.terminate();
    },
    setup() {
      const virtualizerCount = ref(0);
      const listContainer = ref<HTMLElement | null>(null);
      const cachedVirtualRows = shallowRef<VirtualItem[]>([]);

      const cachedTotalSize = ref('0px');

      const rowVirtualizerOptions = computed(() => ({
        count: virtualizerCount.value,
        getScrollElement: () => listContainer.value,
        estimateSize: () => DEFAULT_ROW_HEIGHT_PX,
        overscan: VIRTUAL_OVERSCAN_COUNT,
        useAnimationFrameWithResizeObserver: true,
      }));

      const rowVirtualizer = useVirtualizer(rowVirtualizerOptions);

      watchEffect(
        () => {
          if (rowVirtualizer.value) {
            cachedVirtualRows.value = rowVirtualizer.value.getVirtualItems() ?? [];

            const size = rowVirtualizer.value.getTotalSize();
            cachedTotalSize.value = size !== undefined ? `${size}px` : '0px';
          }
        },
        { flush: 'post' },
      );

      return {
        rowVirtualizer,
        virtualizerCount,
        listContainer,
        cachedVirtualRows,
        cachedTotalSize,
      };
    },
    computed: {
      catalogModal: {
        get(): boolean {
          return this.isShow;
        },
        set() {
          this.closeCatalog();
        },
      },
      getVirtualRows() {
        return this.cachedVirtualRows;
      },
      getTotalSize(): string {
        return this.cachedTotalSize;
      },
      getVisibleItems(): TProductItem[] {
        return this.visibleItems;
      },
      getSearchTerm(): string {
        return this.request.term.toLowerCase().trim();
      },
      getCurrentItems(): TProductItem[] {
        return this.getSearchTerm ? this.searchItems : this.items;
      },
      getSelectionSetter(): Set<number | string> {
        return new Set(this.selection);
      },
      getSelectedIdsDictionary() {
        return new Set(this.selection);
      },
      getListWrapperStyle(): StyleValue {
        return { height: this.getTotalSize };
      },
    },
    methods: {
      closeCatalog() {
        this.selection = [...this.selectedIds];
        this.$emit('close');
      },

      getIsFolderItem(item: TProductItem, index: number): boolean {
        if (this.getSearchTerm) {
          const nextItem = this.getCurrentItems[index + 1];

          if (!nextItem) return false;

          return nextItem.level > item.level;
        }

        return !item.isLeaf;
      },

      // вызывается в миксине resizeEventListener
      /**@public */
      onResize() {
        this.screenWidth = window.innerWidth;
      },
      async loadPage(): Promise<void> {
        try {
          this.isLoading = true;
          const response = await this.fetchTree();
          this.items = response.data.items;
        } catch (error) {
          console.error(error);
        } finally {
          this.isLoading = false;
        }
      },
      fetchTree(): Promise<TArrayAxiosResponse<TProductItem>> {
        if (this.fetcher) return this.fetcher();
        return axios.get('/api/tnved', { params: { sort: 1, ...this.request } });
      },
      search() {
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
          const searchTerm = this.getSearchTerm;

          if (!searchTerm) {
            this.searchItems = [];
            this.visibleItems = this.items
              .filter((item) => item.level === 0)
              .map((item) => ({ ...item, isOpen: false }));
            this.isLoading = false;
            return;
          }

          if (!this.searchWorker) this.initSearchWorker();

          this.isLoading = true;
          this.searchWorker?.postMessage({
            items: JSON.parse(JSON.stringify(this.items)),
            searchTerm: searchTerm,
            textKeys: ['name', 'code'],
            keyField: 'id',
          });
        }, 500);
      },
      initSearchWorker() {
        this.searchWorker = new Worker('/workers/searchWorker.js');
        this.searchWorker.onmessage = (e: MessageEvent) => {
          this.searchItems = e.data.result ?? [];
          this.isLoading = false;
          this.visibleItems = JSON.parse(JSON.stringify(this.searchItems));
          this.openVisibleItems();
        };
      },
      getPreparedCode(index: number) {
        const code = this.preparedVisibleItems[index].code;

        if (code.length === 1) return '';

        return code;
      },
      highlightText(text: string): string {
        if (!this.getSearchTerm || !text) return text;

        const regex = new RegExp(`(${this.getSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(regex, '<mark class="search-highlight">$1</mark>');
      },
      selectAllItems() {
        this.selection = this.getCurrentItems.map((item) => item.id);
      },
      unselectAllItems() {
        this.selection = [];
      },
      select() {
        this.$emit('select', this.selection);
        this.updateSelectedItems();
      },
      updateSelectedItems() {
        this.$emit(
          'updateSelectedItems',
          this.getCurrentItems.filter((item) => this.getSelectedIdsDictionary.has(item.id)),
        );
      },
      checkClick(item: TProductItem) {
        if (this.isCheckDisabled(item)) return;

        if (this.multiple) {
          const indexEl = this.selection.find((x) => x === item.id);
          if (indexEl) {
            this.selection.splice(this.selection.indexOf(indexEl), 1);
          } else {
            this.selection.push(item.id);
          }
        } else {
          this.selection = this.selection[0] === item.id ? [] : [item.id];
        }
      },
      isCheckDisabled(item: TProductItem): boolean {
        return (
          (this.selectGroupsOnly && item.isLeaf) ||
          (this.selectLeafsOnly && !item.isLeaf) ||
          this.isConstraintCode(item)
        );
      },
      isConstraintCode(item: TProductItem): boolean {
        return this.codes.length > 0 && !this.codes.includes(item.code.slice(0, 2));
      },
      getItemKey(item: TProductItem) {
        return `${item.id}-${item.level}`;
      },
      measureElement(el: Element | ComponentPublicInstance | null) {
        if (!this.isShow || !el || !(el instanceof HTMLElement)) {
          return;
        }

        if (el.getBoundingClientRect().height <= 0) {
          return;
        }

        this.rowVirtualizer?.measureElement(el);
      },
      handleArrowClick(index: number) {
        const selectedItem = this.visibleItems[index];
        const mapItems = this.mapItems;
        const itemData = mapItems[selectedItem.id];

        const updatedItem = { ...selectedItem, isOpen: !selectedItem.isOpen };

        if (updatedItem.isOpen) {
          const childrenToAdd = this.getChildrenForDisplay(itemData, mapItems);
          this.visibleItems = [
            ...this.visibleItems.slice(0, index),
            updatedItem,
            ...childrenToAdd,
            ...this.visibleItems.slice(index + 1),
          ];
        } else {
          const childrenToRemove = this.getAllChildrenIds(itemData, mapItems);
          this.visibleItems = this.visibleItems.filter((item) => !childrenToRemove.includes(item.id));
          this.visibleItems = this.visibleItems.map((item) => (item.id === selectedItem.id ? updatedItem : item));
        }
      },
      getAllChildrenIds(
        itemData: TProductItem & { childrenIds: number[] },
        mapItems: Record<number | string, TProductItem & { childrenIds: number[] }>,
      ): (number | string)[] {
        const allChildrenIds: (number | string)[] = [];

        const collectChildren = (parentData: TProductItem & { childrenIds: (number | string)[] }) => {
          if (parentData.childrenIds && parentData.childrenIds.length > 0) {
            parentData.childrenIds.forEach((childId) => {
              allChildrenIds.push(childId);
              const childData = mapItems[childId];
              if (childData) {
                collectChildren(childData);
              }
            });
          }
        };

        collectChildren(itemData);
        return allChildrenIds;
      },
      getChildrenForDisplay(
        itemData: TProductItem & { childrenIds: number[] },
        mapItems: Record<number, TProductItem & { childrenIds: number[] }>,
      ): TProductItem[] {
        return itemData.childrenIds.map((childId) => mapItems[childId]);
      },
      getItemPaddingLeft(item: TProductItem): string {
        return `${12 * item.level}px`;
      },
      openAllItems() {
        this.visibleItems = this.getCurrentItems.map((item) => ({ ...item, isOpen: true }));
        this.openVisibleItems();
      },
      closeAllItems() {
        this.closeVisibleItems();
      },
      openVisibleItems() {
        this.visibleItems.forEach((item) => {
          item.isOpen = true;
        });
      },
      closeVisibleItems() {
        this.visibleItems = this.visibleItems.filter((item) => item.level === 0);

        this.visibleItems.forEach((item) => {
          item.isOpen = false;
        });
      },
      getMapItems(): Record<number, TProductItem & { childrenIds: number[] }> {
        const mapItems: Record<number, TProductItem & { childrenIds: number[] }> = {};
        const parentStack: TProductItem[] = [];

        this.getCurrentItems.forEach((item) => {
          mapItems[item.id] = {
            ...item,
            childrenIds: [],
          };

          while (parentStack.length > 0 && parentStack[parentStack.length - 1].level >= item.level) {
            parentStack.pop();
          }

          if (item.level > 0 && parentStack.length > 0) {
            const currentParent = parentStack[parentStack.length - 1];
            mapItems[currentParent.id].childrenIds.push(item.id);
          }

          if (!item.isLeaf) {
            parentStack.push(item);
          }
        });

        return mapItems;
      },
      openGlobalContextMenu() {
        const globalContextMenuButtonWrapper = this.$refs.globalContextMenuButtonWrapper as HTMLElement;

        this.openContextMenu(this.globalContextMenu, globalContextMenuButtonWrapper);
        this.closeLocalContextMenu();
      },
      closeGlobalContextMenu() {
        this.globalContextMenu.isVisible = false;
      },
      selectLevel(codeLength: number) {
        this.selection = [
          ...new Set([
            ...this.selection,
            ...this.getCurrentItems.filter((item) => item.code.length === codeLength).map((item) => item.id),
          ]),
        ];
      },
      openLocalContextMenu(e: Event, item: TProductItem) {
        this.localContextMenu.selectedItem = item;
        this.openContextMenu(this.localContextMenu, e.target as HTMLElement);
        this.closeGlobalContextMenu();
      },
      closeLocalContextMenu() {
        this.localContextMenu.isVisible = false;
      },
      openContextMenu(context: { isVisible: boolean; position: { right: number; top: number } }, target: HTMLElement) {
        if (!target) return;

        const rect = target.getBoundingClientRect();
        const windowWidth = document.documentElement.scrollWidth;
        const windowScrollTop = document.documentElement.scrollTop;
        context.position = { right: windowWidth - rect.right, top: rect.top + rect.height + windowScrollTop };
        context.isVisible = true;
      },
      handleLocalContextMenuAction(action: LocalContextMenuActions) {
        const selectedItem = this.localContextMenu.selectedItem;

        switch (action) {
          case LocalContextMenuActions.SELECT_ALL_CHILDREN_BY_LEVEL:
            this.selectAllChildrenByLevel(selectedItem);
            break;
          case LocalContextMenuActions.SELECT_ALL_CHILDREN:
            this.selectAllChildren(selectedItem);
            break;
          case LocalContextMenuActions.UNSELECT_ALL_CHILDREN:
            this.unselectAllChildren(selectedItem);
            break;
          default:
            console.log('Unknown action:', action);
        }
      },

      selectAllChildrenByLevel(selectedItem: TProductItem | null) {
        if (!selectedItem) return;

        const itemData = this.mapItems[selectedItem.id];
        if (!itemData || !itemData.childrenIds) return;

        const childrenToSelect = itemData.childrenIds
          .map((childId) => this.mapItems[childId])
          .filter((child) => child && child.level === selectedItem.level + 1);

        const childrenIds = childrenToSelect.map((child) => child.id);
        this.selection = [...new Set([...this.selection, ...childrenIds])];
      },

      selectAllChildren(selectedItem: TProductItem | null) {
        if (!selectedItem) return;

        const itemData = this.mapItems[selectedItem.id];
        if (!itemData || !itemData.childrenIds) return;

        const allChildrenIds = this.getAllChildrenIds(itemData, this.mapItems);

        this.selection = [...new Set([...this.selection, ...allChildrenIds])];
      },

      unselectAllChildren(selectedItem: TProductItem | null) {
        if (!selectedItem) return;

        const itemData = this.mapItems[selectedItem.id];
        if (!itemData || !itemData.childrenIds) return;

        const allChildrenIds = this.getAllChildrenIds(itemData, this.mapItems);

        this.selection = this.selection.filter((id) => !allChildrenIds.includes(id));
      },

      setPreparedVisibleItems() {
        if (this.isLoading) return;

        this.preparedVisibleItems = this.visibleItems.map((item, index) => ({
          ...item,
          isFolder: this.getIsFolderItem(item, index),
          name: this.highlightText(item.name),
          code: this.highlightText(item.code),
        }));
      },

      selectProductsGroups() {
        this.selection = [
          ...new Set([
            ...this.selection,
            ...this.getCurrentItems.filter((item) => item.level === 0 && item.id !== -1).map((item) => item.id),
          ]),
        ];
      },

      getListStyle(value: number): StyleValue {
        return { transform: `translateY(${value}px)` };
      },

      getListItemStyle(value: TProductItem): StyleValue {
        return { paddingLeft: this.getItemPaddingLeft(value) };
      },
    },
    watch: {
      items: {
        handler(newValue: TProductItem[]) {
          this.visibleItems = newValue.filter((item) => item.level === 0);
          this.mapItems = this.getMapItems();
          this.updateSelectedItems();
        },
      },

      getVisibleItems: {
        handler(newValue: TProductItem[]) {
          this.virtualizerCount = newValue.length;
        },
        immediate: true,
      },

      visibleItems: {
        handler() {
          this.setPreparedVisibleItems();
        },
        deep: true,
      },

      selectedIds: {
        handler(newValue: number[]) {
          this.selection = [...newValue];
          this.updateSelectedItems();
        },
        deep: true,
      },
    },
  });
